import React, { forwardRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react'
import { useStore } from 'todistukset-ui/src/vtj/stores/store.util'

import { Heading, Paragraph, Block } from 'suomifi-ui-components'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import PriceListTable, {
  type PriceListTableRow,
} from 'todistukset-ui/src/vtj/components/price-list/PriceListTable.react'
import { TooltipApostille } from 'todistukset-ui/src/vtj/components/order-wizard/InstructionsStep/TooltipApostille'
import { TooltipElectricCertificate } from 'todistukset-ui/src/vtj/components/order-wizard/InstructionsStep/TooltipElectricCertificate'
import { TooltipPaperCertificate } from 'todistukset-ui/src/vtj/components/order-wizard/InstructionsStep/TooltipPaperCertificate'
import { FormContainer } from 'todistukset-ui/src/vtj/components/order-wizard/CommonFormComponents.sc'
import { WizardPriceListContainer } from 'todistukset-ui/src/vtj/components/order-wizard/InstructionsStep/InstructionsStep.sc'

import type { Serialized } from 'common/src/vtj/types/util.type'
import CertificateTypes, {
  VtjOteTodistusType,
  isVtjOteTodistus,
} from 'todistukset-common/src/vtj/CertificateTypes.enum'
import type { TodistusProduct } from 'todistukset-common/src/vtj/types/price-list.type'
import { isEidasIdentifiedUser } from 'todistukset-common/src/vtj/user.utils'
import type { StepComponent } from 'todistukset-ui/src/vtj/components/order-wizard/OrderWizardStep.type'
import EidasCheckYourInfoInlineAlert from 'todistukset-ui/src/vtj/components/order-wizard/EidasCheckYourInfoInlineAlert.react'

const InstructionsStep: StepComponent = forwardRef(function InstructionsStep(
  { titleId, certificateType },
  _ref
) {
  if (!isVtjOteTodistus(certificateType)) {
    throw new Error(
      `Type check failed. This step supports only VtjOteTodistusType but ${certificateType} was given.`
    )
  }

  const {
    common: { loggedInUser },
  } = useStore()
  const isEidas = isEidasIdentifiedUser(loggedInUser.get())

  function mainDescriptionKey(
    certificateType: VtjOteTodistusType,
    isEidas: boolean
  ) {
    const hasEidasKey: { [k: string]: boolean } = {
      [CertificateTypes.ASUINPAIKKATODISTUS]: true,
      [CertificateTypes.SYNTYMATODISTUS]: true,
    }
    if (isEidas && hasEidasKey[certificateType]) {
      return `orderWizardInstructionsStepMainDescription_eidas_${certificateType}`
    }
    return `orderWizardInstructionsStepMainDescription_${certificateType}`
  }

  return (
    <FormContainer>
      <Block variant="section">
        <Heading variant="h2" id={titleId}>
          <LocalizedString stringKey="orderWizardInstructionsStepMainTitle" />
        </Heading>
        <Paragraph>
          <LocalizedString
            stringKey={mainDescriptionKey(certificateType, isEidas)}
          />
        </Paragraph>
        <Paragraph>
          <LocalizedString stringKey="orderWizardInstructionsStepPreliminaries" />
        </Paragraph>
        {isEidas && <EidasCheckYourInfoInlineAlert />}
      </Block>
      <Block variant="section">
        <Heading variant="h3">
          <LocalizedString stringKey="orderWizardInstructionsStepDeliveryTitle" />
        </Heading>
        <Paragraph>
          <LocalizedString
            stringKey={
              isEidas
                ? 'orderWizardInstructionsStepDeliveryTextEidas'
                : 'orderWizardInstructionsStepDeliveryText'
            }
          />
        </Paragraph>
        {/* Currently hidden. Add back when Suomi.fi messages integration is re-implemented. !isEidas && <SuomifiDeliveryInlineAlert /> */}
      </Block>
      <Block variant="section">
        <Heading variant="h3">
          <LocalizedString stringKey="orderWizardInstructionsStepPricingTitle" />
        </Heading>
        <PriceList certificateType={certificateType} />
      </Block>
    </FormContainer>
  )
})

export default InstructionsStep

function typeOfProduct(product: Serialized<TodistusProduct>): CertificateTypes {
  return CertificateTypes[
    product.productType.split('_')[0] as keyof typeof CertificateTypes
  ]
}

/* CertificateSubtypes in todistukset-common/src/vtj/CertificateSubtypes.enum doesn't include
   EU_VIRANOMAISELLE_KAANNOSLOMAKKEELLA. I can't tell if that's intentional or not, so I'll
   just redefine the type here for local use for now. */
enum CertificateSubtypes {
  SAHKOINEN = 'SAHKOINEN',
  EU_VIRANOMAISELLE = 'EU_VIRANOMAISELLE',
  EU_VIRANOMAISELLE_KAANNOSLOMAKKEELLA = 'EU_VIRANOMAISELLE_KAANNOSLOMAKKEELLA',
  APOSTILLE = 'APOSTILLE',
}

function subtypeOfProduct(
  product: Serialized<TodistusProduct>
): CertificateSubtypes {
  return CertificateSubtypes[
    product.productType
      .split('_')
      .slice(1)
      .join('_') as keyof typeof CertificateSubtypes
  ]
}

const PriceList: React.FC<{ certificateType: VtjOteTodistusType }> = observer(
  ({ certificateType }) => {
    const { t } = useTranslation()
    const {
      paymentPriceList: { products },
      common: { lang, loggedInUser },
    } = useStore()
    const isEidas = isEidasIdentifiedUser(loggedInUser.get())

    const priceListTables: [PriceListTableRow, ...PriceListTableRow[]][] =
      useMemo(() => {
        const priceListProducts = products
          .filter(
            (product: Serialized<TodistusProduct>) =>
              typeOfProduct(product) === certificateType
          )
          .filter((product) => !product.productType.endsWith('ELOMAKE'))

        const productsSahkoinen = priceListProducts.filter(
          (product: Serialized<TodistusProduct>) => {
            return subtypeOfProduct(product) === CertificateSubtypes.SAHKOINEN
          }
        )

        const productsPaperinen = priceListProducts.filter(
          (product: Serialized<TodistusProduct>) => {
            return subtypeOfProduct(product) !== CertificateSubtypes.SAHKOINEN
          }
        )

        function getPriceRow(
          product: Serialized<TodistusProduct>
        ): PriceListTableRow {
          const subtype = subtypeOfProduct(product)
          return [
            {
              text: t(`orderWizardPriceListProductLine_${subtype}`, {
                certificate: t(certificateTypeTranslations[certificateType]),
              }),
              tooltip:
                subtype === CertificateSubtypes.APOSTILLE ? (
                  <TooltipApostille />
                ) : undefined,
            },
            {
              price: product.priceWithVatInEuroCents,
              perUnit: t('instructionsStepPerson'),
            },
          ]
        }

        const electronicPrices: [PriceListTableRow, ...PriceListTableRow[]] = [
          [
            {
              text: t('orderWizardPriceListElectronicCertificate'),
              tooltip: <TooltipElectricCertificate />,
            },
            { text: t('priceListPrice') },
          ],
          ...productsSahkoinen.map(getPriceRow),
        ]

        const paperPrices: [PriceListTableRow, ...PriceListTableRow[]] = [
          [
            {
              text: t('orderWizardPriceListPaperCertificate'),
              tooltip: <TooltipPaperCertificate />,
            },
            { text: t('priceListPrice') },
          ],
          ...productsPaperinen.map(getPriceRow),
        ]

        if (isEidas) {
          return [paperPrices]
        }
        return [electronicPrices, paperPrices]
      }, [products, lang]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <WizardPriceListContainer>
        <PriceListTable tables={priceListTables} />
      </WizardPriceListContainer>
    )
  }
)

const certificateTypeTranslations: Record<VtjOteTodistusType, string> = {
  [CertificateTypes.ASUINPAIKKATODISTUS]: 'homeLocationCert',
  [CertificateTypes.AVIOLIITTOTODISTUS]: 'marriageCert',
  [CertificateTypes.ELOSSAOLOTODISTUS]: 'aliveCertificate',
  [CertificateTypes.SIVIILISAATYTODISTUS]: 'maritalStatusCert',
  [CertificateTypes.SYNTYMATODISTUS]: 'birthCert',
}
